/** @format */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { HasErrors } from '../../common/Logic/ServerResponse';
import {
  addAddressDetails,
  addEducationDetails,
  addGuardian,
  addPersonalDetails,
  addStudyDetails,
  deleteDocument,
  deleteProfile,
  fetchAllApplicants,
  fetchApplicantById,
  fetchApplicantByIdentityNumber,
  loginApplicant,
  sendEmailSaveApplication,
  submitApplication,
  uploadDocuments,
  uploadSingleDocument,
} from './ApplicantApi';
import {
  AddressDto,
  ApplicantCreateState,
  ApplicantInitialState,
  ApplicantState,
  DeleteProfileState,
  DocumentDto,
  DocumentsViewDto,
  InstitutionDto,
  LegalGuardianDetailPostDto,
  LoginState,
  PersonalDetailsDto,
  SaveApplicationState,
  StudyDetailsDto
} from './Models';

const initialState: ApplicantInitialState = {
  applicantState: {} as ApplicantState,
  applicantCreateState: {} as ApplicantCreateState,
  loginState: {} as LoginState,
  deleteProfileState: {} as DeleteProfileState,
  saveApplicationState: {} as SaveApplicationState,
};

export const fetchAllApplicantThunk = createAsyncThunk(
  'applicants/all',
  async () => {
    let response: any;
    await fetchAllApplicants()
      .then((data) => {
        response = data.data;
      })
      .catch((error) => {
        response = error.response.data;
      });
    return response;
  }
);

export const addPersonalDetailsThunk = createAsyncThunk(
  'applicants/addPersonalDetails',
  async (payload: PersonalDetailsDto) => {
    let response: any;
    await addPersonalDetails(payload)
      .then((data) => {
        response = data;
      })
      .catch((error) => {
        response = error.response.data;
      });
    return response;
  }
);

export const fetchApplicantByIdentityNumberThunk = createAsyncThunk(
  'applicants/getApplicantByIdentityNumber',
  async (identityNumber: string) => {
    let response: any;
    await fetchApplicantByIdentityNumber(identityNumber)
      .then((data) => {
        response = data;
      })
      .catch((error) => {
        response = error.response.data;
      });
    return response;
  }
);

export const addAddressDetailsThunk = createAsyncThunk(
  'applicants/updateAddress',
  async (payload: AddressDto) => {
    let response: any;
    await addAddressDetails(payload)
      .then((data) => {
        response = data;
      })
      .catch((error) => {
        response = error.response.data;
      });
    return response;
  }
);

export const loginApplicantThunk = createAsyncThunk(
  'applicants/login',
  async () => {
    let response: any;
    await loginApplicant()
      .then((data) => {
        response = data;
      })
      .catch((error) => {
        response = error.response.data;
      });
    return response;
  }
);

export const addEducationDetailsThunk = createAsyncThunk(
  'applicants/addEducation',
  async (payload: InstitutionDto) => {
    let response: any;
    await addEducationDetails(payload)
      .then((data) => {
        response = data;
      })
      .catch((error) => {
        response = error.response.data;
      });
    return response;
  }
);

export const addStudyDetailsThunk = createAsyncThunk(
  'applicants/addStudyDetails',
  async (payload: StudyDetailsDto) => {
    let response: any;
    await addStudyDetails(payload)
      .then((data) => {
        response = data;
      })
      .catch((error) => {
        response = error.response.data;
      });
    return response;
  }
);

export const addGuardianThunk = createAsyncThunk(
  'applicants/addGuardian',
  async (payload: LegalGuardianDetailPostDto) => {
    let response: any;
    await addGuardian(payload)
      .then((data) => {
        response = data;
      })
      .catch((error) => {
        response = error.response.data;
      });
    return response;
  }
);

export const uploadDocumentsThunk = createAsyncThunk(
  'applicants/uploadDocs',
  async (payload: DocumentDto[]) => {
    let response: any;
    await uploadDocuments(payload)
      .then((data) => {
        response = data;
      })
      .catch((error) => {
        response = error.response.data;
      });
    return response;
  }
);

export const fetchApplicantByIdThunk = createAsyncThunk(
  'applicants/fetchApplicantById',
  async (applicantId: number) => {
    let response: any;
    await fetchApplicantById(applicantId)
      .then((data) => {
        response = data;
      })
      .catch((error) => {
        response = error.response.data;
      });
    return response;
  }
);

export const deleteProfileThunk = createAsyncThunk(
  'applicants/deleteProfile',
  async (identityNumber: string) => {
    let response: any;
    await deleteProfile(identityNumber)
      .then((data) => {
        response = data;
      })
      .catch((error) => {
        response = error.response.data;
      });
    return response;
  }
);

export const deleteDocumentThunk = createAsyncThunk(
  'deleteDocument',
  async (payload: number) => {
    let response: any;
    await deleteDocument(payload)
      .then((data) => {
        response = data;
      })
      .catch((error) => {
        response = error.response.data;
      });
    return response;
  }
);



export const uploadSingleDocumentThunk = createAsyncThunk(
  'applicants/uploadSingleDocument',
  async (payload: DocumentsViewDto) => {
    let response: any;
    await uploadSingleDocument(payload)
      .then((data) => {
        response = data;
      })
      .catch((error) => {
        response = error.response.data;
      });
    return response;
  }
);

export const submitApplicationThunk = createAsyncThunk(
  'applicants/submitApplication',
  async (payload: DocumentsViewDto[]) => {
    let response: any;
    await submitApplication(payload)
      .then((data) => {
        response = data;
      })
      .catch((error) => {
        response = error.response.data;
      });
    return response;
  }
);

export const sendEmailSaveApplicationThunk = createAsyncThunk(
  'applicants/sendEmailSaveApplication',
  async () => {
    let response: any;
    await sendEmailSaveApplication()
      .then((data) => {
        response = data;
      })
      .catch((error) => {
        response = error.response.data;
      });
    return response;
  }
);

export const applicantSlice = createSlice({
  name: 'applicant',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(sendEmailSaveApplicationThunk.pending, (state) => {
        state.saveApplicationState.status = 'loading';
        state.saveApplicationState.error = {};
      })
      .addCase(sendEmailSaveApplicationThunk.fulfilled, (state, action) => {
        const hasErrors = HasErrors(action);
        if (hasErrors === 1) {
          state.saveApplicationState.status = 'failed';
          state.saveApplicationState.error = action.payload;
        } else if (hasErrors === 3 || hasErrors === 2) {
          state.saveApplicationState.data = action.payload.data;
          state.saveApplicationState.status = 'idle';
          state.saveApplicationState.error = {};
        }
      })
      .addCase(sendEmailSaveApplicationThunk.rejected, (state, action) => {
        state.deleteProfileState.status = 'failed';
        state.deleteProfileState.error = action.payload;
      });

    builder
      .addCase(deleteProfileThunk.pending, (state) => {
        state.deleteProfileState.status = 'loading';
        state.applicantCreateState.error = {};
      })
      .addCase(deleteProfileThunk.fulfilled, (state, action) => {
        const hasErrors = HasErrors(action);
        if (hasErrors === 1) {
          state.deleteProfileState.status = 'failed';
          state.deleteProfileState.error = action.payload;
        } else if (hasErrors === 3 || hasErrors === 2) {
          state.deleteProfileState.isDeleted = action.payload.data;
          state.deleteProfileState.status = 'idle';
          state.applicantCreateState.error = {};
        }
      })
      .addCase(deleteProfileThunk.rejected, (state, action) => {
        state.deleteProfileState.status = 'failed';
        state.deleteProfileState.error = action.payload;
      });

    builder
      .addCase(uploadSingleDocumentThunk.pending, (state) => {
        state.applicantCreateState.status = 'loading';
        state.applicantCreateState.error = {};
      })
      .addCase(uploadSingleDocumentThunk.fulfilled, (state, action) => {
        const hasErrors = HasErrors(action);
        if (hasErrors === 1) {
          state.applicantCreateState.status = 'failed';
          state.applicantCreateState.error = action.payload;
        } else if (hasErrors === 3 || hasErrors === 2) {
          state.loginState.accessToken.application = action.payload.data;
          state.applicantCreateState.status = 'idle';
          state.applicantCreateState.error = {};
        }
      })
      .addCase(uploadSingleDocumentThunk.rejected, (state, action) => {
        state.applicantCreateState.status = 'failed';
        state.applicantCreateState.error = action.payload;
      });

      builder
      .addCase(deleteDocumentThunk.pending, (state) => {
        state.applicantCreateState.status = 'loading';
        state.applicantCreateState.error = {};
      })
      .addCase(deleteDocumentThunk.fulfilled, (state, action) => {
        const hasErrors = HasErrors(action);
        if (hasErrors === 1) {
          state.applicantCreateState.status = 'failed';
          state.applicantCreateState.error = action.payload;
        } else if (hasErrors === 3 || hasErrors === 2) {
          state.loginState.accessToken.application = action.payload.data;
          state.applicantCreateState.status = 'idle';
          state.applicantCreateState.error = {};
        }
      })
      .addCase(deleteDocumentThunk.rejected, (state, action) => {
        state.applicantCreateState.status = 'failed';
        state.applicantCreateState.error = action.payload;
      });

    builder
      .addCase(submitApplicationThunk.pending, (state) => {
        state.applicantCreateState.status = 'loading';
        state.applicantCreateState.error = {};
      })
      .addCase(submitApplicationThunk.fulfilled, (state, action) => {
        const hasErrors = HasErrors(action);
        if (hasErrors === 1) {
          state.applicantCreateState.status = 'failed';
          state.applicantCreateState.error = action.payload;
        } else if (hasErrors === 3 || hasErrors === 2) {
          state.applicantCreateState.status = 'idle';
          state.applicantCreateState.error = {};
        }
      })
      .addCase(submitApplicationThunk.rejected, (state, action) => {
        state.applicantCreateState.status = 'failed';
        state.applicantCreateState.error = action.payload;
      });

    builder
      .addCase(fetchApplicantByIdThunk.pending, (state) => {
        state.applicantState.status = 'loading';
        state.applicantCreateState.error = {};
      })
      .addCase(fetchApplicantByIdThunk.fulfilled, (state, action) => {
        const hasErrors = HasErrors(action);
        if (hasErrors === 1) {
          state.applicantState.status = 'failed';
          state.applicantState.error = action.payload;
        } else if (hasErrors === 3 || hasErrors === 2) {
          state.applicantState.applicant = action.payload.data;
          state.applicantState.status = 'idle';
          state.applicantCreateState.error = {};
        }
      })
      .addCase(fetchApplicantByIdThunk.rejected, (state, action) => {
        state.applicantState.status = 'failed';
        state.applicantState.error = action.payload;
      });

    builder
      .addCase(uploadDocumentsThunk.pending, (state) => {
        state.applicantCreateState.status = 'loading';
        state.applicantCreateState.error = {};
      })
      .addCase(uploadDocumentsThunk.fulfilled, (state, action) => {
        const hasErrors = HasErrors(action);
        if (hasErrors === 1) {
          state.applicantCreateState.status = 'failed';
          state.applicantCreateState.error = action.payload;
        } else if (hasErrors === 3 || hasErrors === 2) {
          state.loginState.accessToken.application = action.payload.data;
          state.applicantCreateState.status = 'idle';
          state.applicantCreateState.error = {};
        }
      })
      .addCase(uploadDocumentsThunk.rejected, (state, action) => {
        state.applicantCreateState.status = 'failed';
        state.applicantCreateState.error = action.payload;
      });

    builder
      .addCase(addGuardianThunk.pending, (state) => {
        state.applicantCreateState.status = 'loading';
        state.applicantCreateState.error = {};
      })
      .addCase(addGuardianThunk.fulfilled, (state, action) => {
        const hasErrors = HasErrors(action);
        if (hasErrors === 1) {
          state.applicantCreateState.status = 'failed';
          state.applicantCreateState.error = action.payload;
        } else if (hasErrors === 3 || hasErrors === 2) {
          state.loginState.accessToken.application = action.payload.data;
          state.applicantCreateState.status = 'idle';
          state.applicantCreateState.error = {};
        }
      })
      .addCase(addGuardianThunk.rejected, (state, action) => {
        state.applicantCreateState.status = 'failed';
        state.applicantCreateState.error = action.payload;
      });

    builder
      .addCase(addStudyDetailsThunk.pending, (state) => {
        state.applicantCreateState.status = 'loading';
        state.applicantCreateState.error = {};
      })
      .addCase(addStudyDetailsThunk.fulfilled, (state, action) => {
        const hasErrors = HasErrors(action);
        if (hasErrors === 1) {
          state.applicantCreateState.status = 'failed';
          state.applicantCreateState.error = action.payload;
        } else if (hasErrors === 3 || hasErrors === 2) {
          state.loginState.accessToken.application = action.payload.data;
          state.applicantCreateState.status = 'idle';
          state.applicantCreateState.error = {};
        }
      })
      .addCase(addStudyDetailsThunk.rejected, (state, action) => {
        state.applicantCreateState.status = 'failed';
        state.applicantCreateState.error = action.payload;
      });

    builder
      .addCase(addEducationDetailsThunk.pending, (state) => {
        state.applicantCreateState.status = 'loading';
        state.applicantCreateState.error = {};
      })
      .addCase(addEducationDetailsThunk.fulfilled, (state, action) => {
        const hasErrors = HasErrors(action);
        if (hasErrors === 1) {
          state.applicantCreateState.status = 'failed';
          state.applicantCreateState.error = action.payload;
        } else if (hasErrors === 3 || hasErrors === 2) {
          state.loginState.accessToken.application = action.payload.data;
          state.applicantCreateState.status = 'idle';
          state.applicantCreateState.error = {};
        }
      })
      .addCase(addEducationDetailsThunk.rejected, (state, action) => {
        state.applicantCreateState.status = 'failed';
        state.applicantCreateState.error = action.payload;
      });

    builder
      .addCase(loginApplicantThunk.pending, (state) => {
        state.loginState.status = 'loading';
        state.applicantCreateState.error = {};
      })
      .addCase(loginApplicantThunk.fulfilled, (state, action) => {
        const hasErrors = HasErrors(action);
        if (hasErrors === 1) {
          state.loginState.status = 'failed';
          state.loginState.error = action.payload;
        } else if (hasErrors === 3 || hasErrors === 2) {
          state.loginState.accessToken = action.payload.data;
          state.loginState.status = 'idle';
          state.applicantCreateState.error = {};
        }
      })
      .addCase(loginApplicantThunk.rejected, (state, action) => {
        state.loginState.status = 'failed';
        state.loginState.error = action.payload;
      });

    builder
      .addCase(addAddressDetailsThunk.pending, (state) => {
        state.applicantCreateState.status = 'loading';
        state.applicantCreateState.error = {};
      })
      .addCase(addAddressDetailsThunk.fulfilled, (state, action) => {
        const hasErrors = HasErrors(action);
        if (hasErrors === 1) {
          state.applicantCreateState.status = 'failed';
          state.applicantCreateState.error = action.payload;
        } else if (hasErrors === 3 || hasErrors === 2) {
          state.loginState.accessToken.application = action.payload.data;
          state.applicantCreateState.status = 'idle';
          state.applicantCreateState.error = {};
        }
      })
      .addCase(addAddressDetailsThunk.rejected, (state, action) => {
        state.applicantCreateState.status = 'failed';
        state.applicantCreateState.error = action.payload;
      });

    builder
      .addCase(fetchAllApplicantThunk.pending, (state) => {
        state.applicantState.status = 'loading';
        state.applicantCreateState.error = {};
      })
      .addCase(fetchAllApplicantThunk.fulfilled, (state, action) => {
        const hasErrors = HasErrors(action);
        if (hasErrors === 1) {
          state.applicantState.status = 'failed';
          state.applicantState.error = action.payload;
        } else if (hasErrors === 3 || hasErrors === 2) {
          state.applicantState.applicants = action.payload.data;
          state.applicantState.status = 'idle';
          state.applicantCreateState.error = {};
        }
      })
      .addCase(fetchAllApplicantThunk.rejected, (state, action) => {
        state.applicantState.status = 'failed';
        state.applicantState.error = action.payload;
      });

    builder
      .addCase(fetchApplicantByIdentityNumberThunk.pending, (state) => {
        state.applicantCreateState.status = 'loading';
        state.applicantCreateState.error = {};
      })
      .addCase(
        fetchApplicantByIdentityNumberThunk.fulfilled,
        (state, action) => {
          const hasErrors = HasErrors(action);
          if (hasErrors === 1) {
            state.applicantCreateState.status = 'failed';
            state.applicantCreateState.error = action.payload;
          } else if (hasErrors === 3 || hasErrors === 2) {
            state.applicantCreateState.personalDetails = action.payload.data;
            state.applicantCreateState.status = 'idle';
            state.applicantCreateState.error = {};
          }
        }
      )
      .addCase(
        fetchApplicantByIdentityNumberThunk.rejected,
        (state, action) => {
          state.applicantCreateState.status = 'failed';
          state.applicantCreateState.error = action.payload;
        }
      );

    builder
      .addCase(addPersonalDetailsThunk.pending, (state) => {
        state.applicantCreateState.status = 'loading';
        state.applicantCreateState.error = {};
      })
      .addCase(addPersonalDetailsThunk.fulfilled, (state, action) => {
        const hasErrors = HasErrors(action);
        if (hasErrors === 1) {
          state.applicantCreateState.status = 'failed';
          state.applicantCreateState.error = action.payload;
        } else if (hasErrors === 3 || hasErrors === 2) {
          state.applicantCreateState.personalDetails = action.payload.data;
          state.applicantCreateState.status = 'idle';
          state.applicantCreateState.error = {};
        }
      })
      .addCase(addPersonalDetailsThunk.rejected, (state, action) => {
        state.applicantCreateState.status = 'failed';
        state.applicantCreateState.error = action.payload;
      });
  },
});

export const {} = applicantSlice.actions;
export const selectApplicant = (state: RootState) => state.applicant;
export default applicantSlice.reducer;
